import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { KNOWLEDGE_CARDS } from "./knowledgeCardsRegistry";
import CustomKnowledgeCard from "./CustomKnowledgeCard";
import {
  fetchNewCards,
  createNewCard,
  deleteNewCard,
} from "../../api/Conocimiento/newCardsApi";
import { useTranslation } from "react-i18next";
import {
  pageFieldSx,
  pageHeroShellSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";

const PaginaConocimiento = () => {
  const theme = useTheme();
  const { contentMargin, isMobile } = useContext(SidebarContext);
  const { t } = useTranslation();

  const [customCards, setCustomCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadCards = async () => {
      try {
        setLoadingCards(true);
        const cards = await fetchNewCards();
        setCustomCards(Array.isArray(cards) ? cards : []);
      } catch (error) {
        console.error("Error loading new cards:", error);
      } finally {
        setLoadingCards(false);
      }
    };

    loadCards();
  }, []);

  const totalCards = KNOWLEDGE_CARDS.length + customCards.length;

  const canCreateCard = useMemo(
    () => title.trim().length > 0 && content.trim().length > 0,
    [title, content],
  );

  const handleCreateCard = async () => {
    if (!canCreateCard) return;

    setIsSaving(true);
    try {
      const newCard = await createNewCard({
        title: title.trim(),
        content: content.trim(),
      });
      setCustomCards((prev) => [newCard, ...prev]);
      setTitle("");
      setContent("");
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error creating card:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    try {
      await deleteNewCard(cardId);
      setCustomCards((prev) => prev.filter((card) => card.id !== cardId));
    } catch (error) {
      console.error("Error deleting card:", error);
    }
  };

  const fieldSx = pageFieldSx(theme);

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        minHeight: "100dvh",
        width: "100%",
        maxWidth: 1400,
        minWidth: 0,
        pt: { xs: 1.25, md: 2 },
        px: { xs: 1.25, md: 3 },
        pb: { xs: 10, md: 3 },
        ...contentMargin,
      }}
    >
      <Box sx={pageHeroShellSx(theme, { sticky: isMobile })}>
        <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "flex-start" }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  boxShadow: 2,
                }}
              >
                <MenuBookOutlinedIcon />
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: 1.1 }}
                >
                  CRM · NML
                </Typography>
                <Typography
                  component="h1"
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.1,
                    fontSize: { xs: "1.35rem", md: "1.75rem" },
                  }}
                >
                  {t("acidWash.knowledge", { defaultValue: "Conocimiento" })}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 0.75 }}
                >
                  <Chip
                    size="small"
                    label={t("knowledge.cardCount", {
                      defaultValue: "{{count}} fichas",
                      count: totalCards,
                    })}
                    sx={{ fontWeight: 700 }}
                  />
                  {customCards.length > 0 ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("knowledge.customCount", {
                        defaultValue: "{{count}} personalizadas",
                        count: customCards.length,
                      })}
                      sx={{ fontWeight: 700 }}
                    />
                  ) : null}
                </Stack>
              </Box>
            </Stack>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsDialogOpen(true)}
              sx={{
                ...pagePrimaryButtonSx(theme),
                alignSelf: { xs: "stretch", sm: "flex-start" },
                whiteSpace: "nowrap",
              }}
            >
              {t("knowledge.addCard", { defaultValue: "Nueva ficha" })}
            </Button>
          </Stack>
        </Box>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, mt: 0.5, fontSize: { xs: "0.82rem", sm: "0.875rem" } }}
      >
        {t("knowledge.heroHint", {
          defaultValue:
            "Protocolos, checklists e incoterms. Pulsa una ficha para abrirla a pantalla completa.",
        })}
      </Typography>

      {loadingCards ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
          <CircularProgress size={22} />
          <Typography color="text.secondary" variant="body2">
            {t("knowledge.loading", { defaultValue: "Cargando fichas…" })}
          </Typography>
        </Box>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: { xs: 1.25, sm: 2 },
        }}
      >
        {KNOWLEDGE_CARDS.map(({ id, Component }) => (
          <Box key={id} sx={{ minWidth: 0 }}>
            <Component />
          </Box>
        ))}
        {customCards.map((card) => (
          <Box key={card.id} sx={{ minWidth: 0 }}>
            <CustomKnowledgeCard card={card} onDelete={handleDeleteCard} />
          </Box>
        ))}
      </Box>

      <Dialog
        open={isDialogOpen}
        onClose={() => !isSaving && setIsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle>
          {t("knowledge.dialogTitle", { defaultValue: "Nueva ficha" })}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            autoFocus
            label={t("knowledge.fieldTitle", { defaultValue: "Título" })}
            fullWidth
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            sx={fieldSx}
          />
          <TextField
            label={t("knowledge.fieldContent", { defaultValue: "Contenido" })}
            fullWidth
            multiline
            minRows={5}
            value={content}
            onChange={(event) => setContent(event.target.value)}
            helperText={t("knowledge.fieldContentHint", {
              defaultValue: "Un párrafo por línea.",
            })}
            sx={fieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setIsDialogOpen(false)}
            disabled={isSaving}
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            {t("knowledge.cancel", { defaultValue: "Cancelar" })}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateCard}
            disabled={!canCreateCard || isSaving}
            sx={{ borderRadius: 2, ...pagePrimaryButtonSx(theme) }}
          >
            {isSaving
              ? t("knowledge.saving", { defaultValue: "Guardando…" })
              : t("knowledge.create", { defaultValue: "Crear" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaginaConocimiento;
